import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Paper, 
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { tripService, TripRecommendation } from '../api/tripService';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  response?: TripRecommendation; // Add this line 
}

interface ChatPanelProps {
  onDataUpdate: (data: TripRecommendation | null) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ onDataUpdate }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await tripService.sendMessage(input);
      
      const aiMessage: Message = {
        id: Date.now().toString(),
        text: response.user_friendly_response,
        sender: 'ai',
        timestamp: new Date(),
        response: response // Add the whole response as metadata  
      };
      setMessages(prev => [...prev, aiMessage]);
      onDataUpdate(response);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: 'Sorry, there was an error processing your request. Please try again.',
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      onDataUpdate(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="h6" gutterBottom>
          Chat with Trip Mentor
        </Typography>
        
        <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
          <List>
            {messages.map((message) => (
              <React.Fragment key={message.id}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  <ListItemText
                    primary={message.text}
                    secondary={message.timestamp.toLocaleTimeString()}
                    sx={{
                      textAlign: message.sender === 'user' ? 'right' : 'left',
                      bgcolor: message.sender === 'user' ? 'primary.light' : 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      maxWidth: '70%'
                    }}
                />  
          
                </ListItem>
                {message.sender === 'ai' && message.response && message.response.attractions && message.response.attractions.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'row', padding: '0px 16px', alignItems: 'flex-start' }}>
                        {message.response.attractions.map((attr) => (
                          <Box key={attr.name} sx={{ width: 70, height: 70, marginRight: 1, borderRadius: 2, overflow: 'hidden' }}>
                            <a href={attr.google_url} target="_blank" rel="noopener noreferrer">
                              <img src={attr.photo_url} alt={attr.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </a>
                          </Box>
                        ))}
                      </Box>
                  )}
                <Box sx={{ height: '16px' }} />  {/* Spacer */}
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
            {isLoading && (
              <ListItem>
                <CircularProgress size={20} />
              </ListItem>
            )}
          </List>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            disabled={isLoading}
          />
          <IconButton 
            color="primary" 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChatPanel; 